
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { Dashboard } from "@dto/Dashboard";
import { DashboardRightType, FullRightAccess, ReadRightAccess, hasGlobalRightAccess } from "@dto/Users";
import { getSelectedDashboardKey, updateStorageSelection } from "@utils/localStorage";
import { TW_AppSidebarMenuIcon, TW_AppSidebarMenuText, TW_AppSidebarMenuTooltipText, TW_DashboardListItem, TW_DashboardListItemContainer, TW_DashboardListItemDisabled, TW_ListItemArrow, getWsButtonStyle } from "@utils/twClasses";
import { ArrowDown2, ArrowRight2, Element3 } from "iconsax-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore, useUserStore } from "src/store";
import DashboardListItem from "./DashboardListItem";
import NewDashboardButton from "./NewDashboardButton";


interface DashboardListProps {
  onMobileClose?: () => void;
}

function DashboardList({ onMobileClose }: DashboardListProps) {

  const dashboards = useModelStore(state => state.dashboards);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const userRole = useModelStore(state => state.userRole);
  const user = useUserStore(state => state.user);
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const selectedDashboard = useModelStore(state => state.selectedDashboard);
  const selectDashboard = useModelStore(state => state.selectDashboard);
  const updateSelectedDashboard = useModelStore(state => state.updateSelectedDashboard);
  const updateDashboards = useModelStore(state => state.updateDashboards);
  const [isHover, setIsHover] = useState(false);
  const workspace = useModelStore(state => state.selectedWorkspace);
  const workspaceKey = workspace?._key ?? "";

  useEffect(() => {
    const hasSelectedDashboard = selectedDashboard !== null || getSelectedDashboardKey(workspaceKey) !== null;
    if (hasSelectedDashboard) {
      setIsOpen(true);
    }
  }, [selectedDashboard, workspaceKey]);

  const disabled = false; // dashboards?.length === 0;
  const onArrowClick = (e: React.MouseEvent<SVGElement | HTMLDivElement | HTMLButtonElement>) => {
    if (disabled) {
      return;
    }
    e.stopPropagation();
    setIsOpen(!isOpen);
  }

  const onMouseEnter = () => {
    if (disabled) {
      return;
    }
    setIsHover(true);
  }

  const onMouseLeave = () => {
    if (disabled) {
      return;
    }
    setIsHover(false);
  }

  const newDashboard = (dashboard: Dashboard) => {
    const newDashboards = [...(dashboards || []), dashboard].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    updateDashboards(newDashboards);
    selectDashboard(dashboard);
    updateSelectedDashboard(dashboard);
    updateStorageSelection(workspace?._key, null, null, dashboard._key);
  }

  let style = isHover ? getWsButtonStyle(wsHoverColor) : getWsButtonStyle('white');
  const hasFullDashboardRight = hasGlobalRightAccess(user, userRole, DashboardRightType, FullRightAccess);
  const sortedDashboards = [...(dashboards ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <>
      <div className={TW_DashboardListItemContainer}>
        <div className={(disabled ? TW_DashboardListItemDisabled : TW_DashboardListItem)} onClick={onArrowClick} style={style} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>

          {disabled && <TooltipProvider>
            <Tooltip delayDuration={100}>
              <TooltipTrigger onClick={onArrowClick} className={"flex flex-row justify-start items-center gap-2 w-full cursor-not-allowed"}>
                <Element3 className={TW_AppSidebarMenuIcon + " cursor-not-allowed"} />
                <p className={TW_AppSidebarMenuText + " cursor-not-allowed"}>{t("app.sidebar.button.dashboard")}</p>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex flex-col">
                  <span className={TW_AppSidebarMenuTooltipText}>{t("app.coming.soon")}</span>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          }
          {!disabled &&
            <div onClick={onArrowClick} className="flex flex-row justify-start items-center gap-2 w-full">
              <Element3 className={TW_AppSidebarMenuIcon} />
              <p className={TW_AppSidebarMenuText}>{t("app.sidebar.button.dashboard")}</p>
            </div>
          }
          {!isOpen && <ArrowRight2 className={TW_ListItemArrow + " " + (disabled ? "cursor-not-allowed" : "")} onClick={onArrowClick} />}
          {isOpen && <ArrowDown2 className={TW_ListItemArrow + " " + (disabled ? "cursor-not-allowed" : "")} onClick={onArrowClick} />}
        </div>
        {isOpen && sortedDashboards.map((dashboard) => {
          const hasCurrentDashboardReadRight = dashboard.ownerKey === user?._key || !dashboard.ownerKey || hasGlobalRightAccess(user, userRole, DashboardRightType, ReadRightAccess, dashboard._key, true);
          if (!hasCurrentDashboardReadRight) {
            return null;
          }
          return <DashboardListItem key={dashboard._key} dashboard={dashboard} onMobileClose={onMobileClose} />
        })}
        {isOpen && hasFullDashboardRight && <NewDashboardButton onNewDashboard={newDashboard} />}
      </div>

    </>
  )
};

export default DashboardList;